import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import Logo from "@/components/Logo";
import { Mail, CheckCircle2, ArrowLeft } from "lucide-react";

import { BACKEND_URL as API } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send the reset link. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12" style={{ backgroundColor: "var(--ink-1)" }}>
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo size="md" variant="lockup" dark linkTo="/" /></div>
        <Card data-testid="forgot-password-page">
          <CardHeader>
            <CardTitle>{sent ? "Check your inbox" : "Reset your password"}</CardTitle>
            <CardDescription>
              {sent
                ? "If an account exists for that email, we just sent a reset link. It expires in 1 hour."
                : "Enter the email tied to your ArmIQ account and we'll send a one-time link to set a new password."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                  <span>Email sent to <strong className="text-white">{email}</strong>. Check spam if you don't see it within a minute.</span>
                </div>
                <Link to="/login">
                  <Button variant="outline" className="w-full" data-testid="forgot-back-to-login">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to sign-in
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email address</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                      id="email" type="email" required autoFocus
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="coach@yourteam.edu" className="pl-9"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                {error && (
                  <div className="text-sm text-rose-300 rounded border border-rose-500/30 bg-rose-500/10 p-2">{error}</div>
                )}
                <Button type="submit" className="w-full" disabled={busy || !email} data-testid="forgot-submit">
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
                <Link to="/login" className="block text-center text-xs text-white/60 hover:text-white">
                  Remembered it? Sign in →
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
